import type { IBioCatchConfig, IDataSecurityAdditionalConfig } from '@nab/x-types';
/* eslint-disable dot-notation */
// import React from 'react';
import { DataSecurityMgr } from '../DataSecurityMgr';
import { sdkLoggerInterface as log } from '../../Logger/Logger';

/**
 * @class BCManager
 * The BC Manager is used to enable the data security that looks at the user behaviour on the page and
 * send back regular calls to the Security API to analyse the user usage data and detect fraudulent transactions.
 * The class is responsible to instantiate the library and set unique session ID.
 * This Session ID will be passed down to the miniapp.
 * The miniapp will be able to add this session ID in the headers for the API calls it makes to downstream system.
 *
 * @example
 * To set this up in a shell you'll need to know the Library URL,
 * So that when the AppLoader is loading and setting up the miniapp it is able to create the class and set the script tag up on the dom.
 *
 * **NOTE** The Shell only needs to follow the example below, as this initialisation happens within the AppLoader By default
 *
 * ```typescript
 * const bc = new BCManager("URL TO THE LIBRARY");
 * // this return the bio catch case id string now.
 * const bc: string = await bc.getData();
 * // sets the context for the bio catch calls
 * const response= bc.setContext('myContext')
 * if(response) {
 *   log.info('context set successfully')
 * } else {
 *   log.warn('context not set as the library is not initialised')
 * }
 * ```
 *
 * @example
 * Shell can configure the Bio Catch library via the the service configuration.
 * The configuration allow to set an library URL and an _optional_ config value that will allow then to re-use any existing **CSID**.
 * By default the CSID will be auto generated as unique GUID, if the config is not provided.
 * To enable this Library in your application.
 *
 * ```typescript
 * initiRuntime({
 *   ...
 *   userConfig: {
 *     type: 'customer',
 *     org: 'nab',
 *     sessionId: "exiting-sessionId" //Optional
 *   },
 *   servicesConfig: {
 *     dataSecurity: {
 *       bc {
 *         LibraryUrl: 'https://bc.library.url',
 *
 *        // Optional configuration to send in any existing CSID
 *        // This configValue is still here just for backward-compatibility.
 *        // Please set the sessionId in userConfig above instead. <start>
 *        configValue: 'exiting-cs-id-value' // Optional
 *        // <end>
 *       }
 *     }
 *   }
 *   ...
 * })
 * ```
 * @example
 * Miniapp(s) can use this objects passed via the as `user` property to App.tsx, or via the MiniappContext
 * Irrespective of whether the library is passed or not, the class for BC will be passed down and accessible via the `await user.dataSecurity('bc').getData()` call,
 *  and can be used to see if the promise resolution is positive or failure and handle their case accordingly
 *
 * ```typescript
 * const App = function({ ..., user}) {
 *   ...
 *   let data = '';
 *   try{
 *      const bcFuncs = user.dataSecurity('bc');
 *      data  = await bcFuncs.getData();                        // returns the BC CSID value
 *
 *      // sets the context for the bio catch calls
 *      const response = bcFuncs.setContext('myContext')
 *      if(response) {
 *         log.info('context set successfully')
 *      } else {
 *        log.warn('context not set as the library is not initialised')
 *      }
 *   }
 *   catch(e) {
 *      warn('unable to get the data', e);
 *   }
 *
 *  // using the data.
 *   fetch({
 *     url: 'https://downstream.system.com/server/to/call',
 *     headers: {
 *       ...
 *       ...(data && { 'x-nab-custom-header-CSID': data })
 *     }
 *   })
 *   ...
 * }
 * ```
 */
class BCManager extends DataSecurityMgr<IBioCatchConfig | string, string> {
    private GLOBAL_BC_VAR_NAME = 'cdApi';

    private CSID: string = '';

    /**
     * Used to set the configuration and the library URI to load the unique ID for BC data.
     * @param cdnUrl Library URI to be loaded. Usually a CDN path.
     * @param config The unique ID provided to start the BC session or an object with IBioCatchConfig interface to set the customerBrand and csId
     */
    constructor(cdnUrl: string, config?: IBioCatchConfig | string, additionalConfig?: IDataSecurityAdditionalConfig) {
        super(cdnUrl, config, additionalConfig);
        this.initialise();
    }

    /**
     * Required to be initialised on load of the application.
     * This will load the BC library using the specified cdnURL.
     */
    protected async initialise(): Promise<boolean> {
        /**
         * Added this condition to support preloaded bio catch library by the channel before initialising the x-runtime.
         */
        let isScriptLoaded = !!window[this.GLOBAL_BC_VAR_NAME];
        if (!isScriptLoaded) {
            isScriptLoaded = await this.loadLibrary();
        }
        if (isScriptLoaded) {
            if (typeof this.config === 'string') {
                this.setCSID(this.config);
            } else if (typeof this.config === 'object') {
                this.setCSID(this.config.csId);
                this.setCustomerBrand(this.config.customerBrand);
            } else {
                log.warn(`No configValue provided`);
                return false;
            }
            return true;
        }
        return false;
    }

    /**
     * Sets the unique session ID based on the uniqueID provided in the constructor.
     * If no custom unique ID is provided, get the common sessionId which has been generated on shell initialization.
     */
    private setCSID(csId: string) {
        const windowBC = window[this.GLOBAL_BC_VAR_NAME];
        this.CSID = csId;

        if (typeof this.config === 'object') {
            this.config.csId = csId;
        } else {
            this.config = csId;
        }

        if (windowBC?.setCustomerSessionId) {
            windowBC.setCustomerSessionId(this.CSID);
        }
    }

    private setCustomerBrand(customerBrand: string) {
        try {
            const windowBC = window[this.GLOBAL_BC_VAR_NAME];

            if (typeof this.config === 'object' && customerBrand) {
                this.config.customerBrand = customerBrand;
            }
            if (windowBC?.setCustomerBrand && customerBrand) {
                windowBC.setCustomerBrand(customerBrand);
            }
        } catch (error) {
            log.warn(`Failed to set customer brand name. ${error}`);
        }
    }

    private appendInteractionPrefix(context: string) {
        // if context already starts with the interaction prefix, do not add it again
        const bShouldAppend = this.additionalConfig?.interactionPrefix?.length > 0 && !context.startsWith(this.additionalConfig.interactionPrefix);

        if (!bShouldAppend) {
            return context;
        }

        return `${this.additionalConfig.interactionPrefix}_${context}`;
    }

    /**
     * The data generated by the BC library can be retrieved here.
     * An error will be thrown the bc function is not found in the window or if no CSID is set
     */
    public async getData(): Promise<string> {
        if (window[this.GLOBAL_BC_VAR_NAME] && this.CSID) {
            return this.CSID;
        }
        return Promise.reject(new Error('Data security feature for BC not initialised.'));
    }

    /**
     * @function setContext
     * @description Use this function to set or change the context of the BioCatch fraud library set-up
     * @param context The string value that will be the used to set the name of the context.
     */
    public setContext(context: string, appId?: string): boolean {
        if (window[this.GLOBAL_BC_VAR_NAME] && this.CSID && typeof window[this.GLOBAL_BC_VAR_NAME].changeContext === 'function') {
            const contextToUpdate = this.appendInteractionPrefix(context);
            window[this.GLOBAL_BC_VAR_NAME].changeContext(contextToUpdate);
            return true;
        }
        log.warn('Data security library not initialised');
        return false;
    }

    public updateData(csId?: string): boolean {
        this.setCSID(csId);
        if (window[this.GLOBAL_BC_VAR_NAME]) {
            return true;
        }
        log.warn('Data security feature for BC not initialised.');
        return false;
    }
}

export { BCManager };
